import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";

// Dual-channel OTP (TextBee SMS + Resend email). The OTP is never shown on screen.
export function OtpPanel({ phone, email, role, extra = {}, onVerified, onBack, title = "Verify OTP" }) {
  const [requested, setRequested] = useState(false);
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [masked, setMasked] = useState({ phone: "", email: "" });
  const [cooldown, setCooldown] = useState(0);
  const [channelMsg, setChannelMsg] = useState("");
  const started = useRef(false);

  const request = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone, email: email || null, role });
      setMasked({ phone: data.masked_phone, email: data.masked_email });
      setRequested(true);
      setCooldown(30);
      if (data.sms_sent && data.email_sent) setChannelMsg("OTP sent to your mobile and email.");
      else if (data.email_sent && !data.sms_sent) setChannelMsg("OTP sent to your email. SMS delivery failed.");
      else if (data.sms_sent && !data.email_sent) setChannelMsg(email ? "OTP sent by SMS. Email delivery failed." : "OTP sent to your mobile.");
      else setChannelMsg("We couldn't send your OTP. Please try again.");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setSending(false); }
  };

  useEffect(() => {
    if (!started.current) { started.current = true; request(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async () => {
    if (otp.length < 4) return toast.error("Enter the OTP");
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, otp, role, email: email || null, ...extra });
      onVerified(data);
    } catch (e) { toast.error(apiError(e)); } finally { setVerifying(false); }
  };

  return (
    <div className="space-y-3" data-testid="otp-panel">
      <div className="flex items-center gap-2">
        {onBack && <button onClick={onBack} className="w-8 h-8 flex items-center justify-center" data-testid="otp-back"><ArrowLeft size={18} /></button>}
        <h2 className="font-display font-bold text-lg text-ink">{title}</h2>
      </div>
      <p className="text-xs text-ink-600" data-testid="otp-sent-msg">
        {channelMsg} {requested && <span className="block mt-1">SMS: <b>{masked.phone}</b>{masked.email ? <> · Email: <b>{masked.email}</b></> : null}</span>}
      </p>
      <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter 6-digit OTP"
        className="w-full bg-surface rounded-xl px-3.5 py-3 text-sm tracking-[0.3em] font-bold outline-none focus:ring-2 ring-coral" data-testid="otp-input" />
      <button onClick={verify} disabled={verifying} className="w-full bg-coral hover:bg-coral-hover text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 tap" data-testid="verify-otp-btn">
        {verifying ? <Loader2 className="animate-spin" size={18} /> : <><ShieldCheck size={18} /> Verify &amp; Continue</>}
      </button>
      <button onClick={request} disabled={cooldown > 0 || sending} className="w-full text-xs font-semibold text-coral disabled:text-ink-600" data-testid="resend-otp-btn">
        {sending ? "Sending..." : cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
      </button>
    </div>
  );
}
